import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Clock, AlertTriangle, Layers, WifiOff, Ban, X, RefreshCw } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './AlertsStrip.css';

function AlertsStrip({ departmentCode }) {
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [retrying, setRetrying] = useState({});
  const [dismissedFailures, setDismissedFailures] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('dismissedFailures') || '[]');
    } catch { return []; }
  });

  const { data: jobsData, refetch: refetchJobs } = useQuery({
    queryKey: ['scraper', 'jobs', 'alerts'],
    queryFn: () => apiClient.get('/api/scraper/jobs?limit=200'),
    refetchInterval: 15000,
  });

  const { data: gapsData } = useQuery({
    queryKey: ['coverage', 'gaps'],
    queryFn: () => apiClient.get('/api/coverage/gaps'),
    refetchInterval: 30000,
  });

  const { data: detailData } = useQuery({
    queryKey: ['scraper', 'detail', 'queue', 'alerts'],
    queryFn: () => apiClient.get('/api/scraper/detail/queue'),
    refetchInterval: 15000,
  });

  const { data: throttleData } = useQuery({
    queryKey: ['scraper', 'throttle'],
    queryFn: () => apiClient.get('/api/scraper/throttle'),
    refetchInterval: 15000,
  });

  const { data: workerData } = useQuery({
    queryKey: ['scraper', 'worker', 'status', 'alerts'],
    queryFn: () => apiClient.get('/api/scraper/worker/status'),
    refetchInterval: 15000,
  });

  const { data: healthData } = useQuery({
    queryKey: ['scraper', 'jobs', 'health', 'alerts'],
    queryFn: () => apiClient.get('/api/scraper/jobs/health'),
    refetchInterval: 15000,
  });

  const wasOnlineRef = useRef(null);
  const isWorkerOffline = workerData?.workers?.length > 0 && !workerData?.hasOnlineWorker;

  const zeroStreakRef = useRef({ scprs: 0, scprs_detail: 0 });

  useEffect(() => {
    if (wasOnlineRef.current === null) {
      wasOnlineRef.current = !isWorkerOffline;
      return;
    }
    if (wasOnlineRef.current && isWorkerOffline) {
      if (typeof window.addNotification === 'function') {
        window.addNotification({
          type: 'error',
          message: 'Scraper worker offline - jobs will not process'
        });
      }
    }
    wasOnlineRef.current = !isWorkerOffline;
  }, [isWorkerOffline]);

  useEffect(() => {
    const threshold = parseInt(healthData?.consecutive_zero_results?.threshold || 3);
    const curr = {
      scprs: parseInt(healthData?.consecutive_zero_results?.scprs || 0),
      scprs_detail: parseInt(healthData?.consecutive_zero_results?.scprs_detail || 0),
    };
    const prev = zeroStreakRef.current;
    if (prev.scprs < threshold && curr.scprs >= threshold) {
      if (typeof window.addNotification === 'function') {
        window.addNotification({
          type: 'error',
          message: `Scraper saved 0 POs on ${curr.scprs} consecutive scrapes — check persist logs`,
        });
      }
    }
    if (prev.scprs_detail < threshold && curr.scprs_detail >= threshold) {
      if (typeof window.addNotification === 'function') {
        window.addNotification({
          type: 'error',
          message: `Detail extraction saved 0 POs on ${curr.scprs_detail} consecutive jobs — check persist logs`,
        });
      }
    }
    zeroStreakRef.current = curr;
  }, [healthData]);

  const allJobs = jobsData?.jobs || [];
  const deptJobs = departmentCode
    ? allJobs.filter(j => {
        const p = typeof j.params === 'string' ? JSON.parse(j.params) : (j.params || {});
        return p.departmentCode === departmentCode;
      })
    : allJobs;

  // Only count real failures: last 24h, not dismissed, not bug-era cleanup markers.
  const recentFailedJobs = deptJobs.filter(j => {
    if (j.status !== 'failed') return false;
    if (dismissedFailures.includes(j.id)) return false;
    if (j.error && j.error.startsWith('Bug-era pollution')) return false;
    if (j.completed_at) {
      const hoursAgo = (Date.now() - new Date(j.completed_at).getTime()) / 3600000;
      if (hoursAgo > 24) return false;
    }
    return true;
  });
  const failedJobs = recentFailedJobs.length;

  const allGaps = gapsData?.windows || [];
  const gapCount = departmentCode
    ? allGaps.filter(g => g.department_code === departmentCode).length
    : allGaps.length;

  const pendingDetails = parseInt(detailData?.pending || 0);
  const isThrottled = throttleData?.blocked === true;
  const throttleMinutes = isThrottled && throttleData?.blocked_until
    ? Math.max(0, Math.ceil((new Date(throttleData.blocked_until) - Date.now()) / 60000))
    : 0;

  const zeroResults = healthData?.consecutive_zero_results || {};
  const zeroThreshold = parseInt(zeroResults.threshold || 3);
  const zeroScprs = parseInt(zeroResults.scprs || 0);
  const zeroDetail = parseInt(zeroResults.scprs_detail || 0);

  const alerts = [];

  if (isWorkerOffline) {
    alerts.push({ key: 'worker-offline', icon: WifiOff, label: 'Worker offline', cls: 'sd-alerts-danger' });
  }
  if (zeroScprs >= zeroThreshold) {
    alerts.push({ key: 'zero-scprs', icon: Ban, label: `${zeroScprs} consecutive scrapes saved 0 POs`, cls: 'sd-alerts-danger' });
  }
  if (zeroDetail >= zeroThreshold) {
    alerts.push({ key: 'zero-detail', icon: Ban, label: `${zeroDetail} consecutive detail jobs saved 0 POs`, cls: 'sd-alerts-danger' });
  }
  if (failedJobs > 0) {
    alerts.push({
      key: 'failed',
      icon: AlertCircle,
      label: `${failedJobs} failed job${failedJobs !== 1 ? 's' : ''}`,
      cls: 'sd-alerts-danger sd-alerts-clickable',
      onClick: () => setShowFailedModal(true),
    });
  }
  if (isThrottled) {
    alerts.push({ key: 'throttle', icon: Clock, label: `Throttle blocked${throttleMinutes > 0 ? ` (${throttleMinutes}m)` : ''}`, cls: 'sd-alerts-warning' });
  }
  if (gapCount > 0) {
    alerts.push({ key: 'gaps', icon: AlertTriangle, label: `${gapCount} coverage gap${gapCount !== 1 ? 's' : ''}`, cls: 'sd-alerts-caution' });
  }
  if (pendingDetails > 0) {
    alerts.push({ key: 'details', icon: Layers, label: `${pendingDetails} PO${pendingDetails !== 1 ? 's' : ''} need detail extraction`, cls: 'sd-alerts-info' });
  }

  if (alerts.length === 0) return null;

  const dismissAllFailures = () => {
    const ids = recentFailedJobs.map(j => j.id);
    const next = [...dismissedFailures, ...ids];
    setDismissedFailures(next);
    localStorage.setItem('dismissedFailures', JSON.stringify(next));
    setShowFailedModal(false);
  };

  const dismissOne = (id) => {
    setDismissedFailures(prev => {
      const next = [...prev, id];
      localStorage.setItem('dismissedFailures', JSON.stringify(next));
      return next;
    });
  };

  const retryFailedJob = async (jobId) => {
    setRetrying(prev => ({ ...prev, [jobId]: true }));
    try {
      const resp = await apiClient.post(`/api/scraper/jobs/${jobId}/retry`, {});
      console.log('[Retry] response:', resp);
      const msg = resp?.skipped
        ? `Already pending (duplicate window)`
        : `Re-queued as job ${resp?.jobId?.slice(0, 8) || 'new'}`;
      if (typeof window.addNotification === 'function') {
        window.addNotification({ type: 'success', message: msg });
      } else {
        alert(msg);
      }
      dismissOne(jobId);
      refetchJobs();
    } catch (err) {
      console.error('[Retry] error:', err);
      const msg = `Retry failed: ${err?.message || err}`;
      if (typeof window.addNotification === 'function') {
        window.addNotification({ type: 'error', message: msg });
      } else {
        alert(msg);
      }
    } finally {
      setRetrying(prev => { const n = { ...prev }; delete n[jobId]; return n; });
    }
  };

  return (
    <>
      <div className="sd-alerts-strip">
        {alerts.map(a => {
          const Icon = a.icon;
          return (
            <span
              key={a.key}
              className={`sd-alerts-chip ${a.cls}`}
              onClick={a.onClick}
              style={a.onClick ? { cursor: 'pointer' } : undefined}
            >
              <Icon size={14} />
              {a.label}
            </span>
          );
        })}
      </div>

      {showFailedModal && (
        <div className="sd-modal-backdrop" onClick={() => setShowFailedModal(false)}>
          <div className="sd-modal sd-failed-modal" onClick={e => e.stopPropagation()}>
            <div className="sd-modal-header">
              <h3><AlertCircle size={18} /> Failed Jobs ({recentFailedJobs.length})</h3>
              <div className="sd-modal-actions">
                <button className="sd-btn sd-btn-ghost" onClick={dismissAllFailures}>Dismiss All</button>
                <button className="sd-btn sd-btn-close" onClick={() => setShowFailedModal(false)}>
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="sd-modal-body">
              {recentFailedJobs.length === 0 ? (
                <div className="sd-modal-empty">No recent failures</div>
              ) : (
                <div className="sd-failed-list">
                  {recentFailedJobs.map(job => {
                    const params = typeof job.params === 'string' ? JSON.parse(job.params) : (job.params || {});
                    return (
                      <div key={job.id} className="sd-failed-item">
                        <div className="sd-failed-info">
                          <span className="sd-failed-dept">{params.departmentCode || 'Unknown'}</span>
                          <span className="sd-failed-dates">{params.dateFrom} → {params.dateTo}</span>
                          <span className="sd-failed-type">{params.acquisitionType || 'All'}</span>
                        </div>
                        <div className="sd-failed-error" title={job.error}>
                          {(job.error || 'Unknown error').substring(0, 80)}
                        </div>
                        <div className="sd-failed-actions">
                          <button
                            className="sd-btn sd-btn-retry"
                            onClick={() => retryFailedJob(job.id)}
                            disabled={!!retrying[job.id]}
                            title="Re-queue this job"
                          >
                            <RefreshCw size={12} className={retrying[job.id] ? 'jq-spin' : ''} />
                            {retrying[job.id] ? ' Retrying...' : ' Retry'}
                          </button>
                          <button className="sd-btn sd-btn-dismiss" onClick={() => dismissOne(job.id)} title="Dismiss this failure">
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AlertsStrip;
